import React, { useState, useRef, useCallback } from 'react';
import Button from '../components/Button';
import FactoryFlashMode from '../components/FactoryFlashMode';
import './OTAManagementPage.css';

/**
 * Factory Flash: unattended flashing station for the factory line. Pick a
 * firmware build per goggle model, press Start, and every goggle plugged into
 * the USB hub is detected and flashed with no further clicks.
 * Gated by the `factory_flash` product (Remote Config `offboard_factory_flash`).
 */
const FactoryFlashPage = () => {
  const [isSupported] = useState('serial' in navigator);
  const [flashLogs, setFlashLogs] = useState([]);
  const logsEndRef = useRef(null);

  const addLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });

    setFlashLogs(prev => [...prev.slice(-500), { timestamp, message, type, id: Date.now() + Math.random() }]);

    // Auto-scroll the log box only; scrolling the page would pull the cards
    // out of view on every line.
    setTimeout(() => {
      const container = logsEndRef.current?.parentElement;
      if (container) container.scrollTop = container.scrollHeight;
    }, 50);
  }, []);

  const clearLogs = () => setFlashLogs([]);

  const exportLogs = () => {
    const logText = flashLogs.map(log => `[${log.timestamp}] [${log.type.toUpperCase()}] ${log.message}`).join('\n');
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `factory-flash-log-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="ota-management-page">
      <div className="ota-container">
        <div className="factory-page-header">
          <h1>Factory Flash</h1>
          <p>Flash every REKKIE goggle plugged into this station, automatically</p>
        </div>

        {!isSupported && (
          <div className="ota-warning">
            <h3>Use Chrome or Edge to flash firmware</h3>
            <p>Connecting to goggles requires the Web Serial API, which is only available in Chromium-based browsers.</p>
          </div>
        )}

        {isSupported && (
          <>
            <FactoryFlashMode addLog={addLog} />

            <div className="ota-section logs-section">
              <div className="logs-header">
                <h3>Flash Log</h3>
                <div className="logs-actions">
                  <Button variant="outline" size="small" onClick={clearLogs} disabled={flashLogs.length === 0}>
                    Clear
                  </Button>
                  <Button variant="outline" size="small" onClick={exportLogs} disabled={flashLogs.length === 0}>
                    Export
                  </Button>
                </div>
              </div>
              <div className="logs-container">
                {flashLogs.length === 0 ? (
                  <div className="logs-empty">Logs will appear here during flashing...</div>
                ) : (
                  flashLogs.map(log => (
                    <div key={log.id} className={`log-entry log-${log.type}`}>
                      <span className="log-timestamp">[{log.timestamp}]</span>
                      <span className="log-message">{log.message}</span>
                    </div>
                  ))
                )}
                <div ref={logsEndRef} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FactoryFlashPage;
